/* eslint-disable max-lines */
/* eslint-disable import/prefer-default-export */
/* eslint-disable complexity */
/* eslint-disable max-statements */
import { useMemo, useState, useRef, useCallback, useEffect } from 'react';
import { uid } from 'uid';
import { type DSFastListT } from '@elliemae/ds-fast-list';

import { useMemoMergePropsWithDefault, useValidateTypescriptPropTypes } from '@elliemae/ds-props-helpers';
import { defaultProps } from '../ComboBoxCTX.js';
import type { DSComboboxT } from '../react-desc-prop-types.js';
import { ComboboxPropTypes } from '../react-desc-prop-types.js';
import type { DSComboboxInternalsT } from '../sharedTypes.js';
import { DSComboBoxName } from '../theming.js';
import { getFirstOption, isSelectedValueEmpty } from '../utils/listHelper.js';
import { useCorrectOptions } from './useCorrectOptions.js';
import { useOptionsPerSection } from './useOptionsPerSection.js';

export const useComboBox = (props: DSComboboxT.Props): DSComboboxInternalsT.ComboBoxContextT => {
  const propsWithDefaults = useMemoMergePropsWithDefault<DSComboboxT.InternalProps>(props, defaultProps);
  useValidateTypescriptPropTypes(propsWithDefaults, ComboboxPropTypes, DSComboBoxName);
  const [showPopover, setShowPopover] = useState<boolean>(false);
  const [referenceElement, setReferenceElement] = useState<HTMLElement | null>(null);

  const [inputValue, setInputValue] = useState<string>('');
  const [hasFocus, setHasFocus] = useState<boolean>(false);

  const internalRef = useRef<HTMLInputElement>(null);
  const wrapperListRef = useRef<HTMLDivElement>(null);

  const selectedOptionsRef = useRef<HTMLDivElement>(null);
  const controlsWrapperRef = useRef<HTMLDivElement>(null);
  const pillGroupRef = useRef<HTMLDivElement>(null);

  const { id, selectedValues, isMenuOpen, onMenuChange, applyAriaDisabled } = propsWithDefaults;

  const instanceUid = useMemo(() => id || uid(5), [id]);

  const menuState = useMemo(() => {
    if (isMenuOpen !== undefined) return isMenuOpen;
    return showPopover;
  }, [showPopover, isMenuOpen]);

  // ---------------------------------------------------------------------------
  // Options with creatable option + filtered by selected
  // ---------------------------------------------------------------------------

  const correctOptions = useCorrectOptions(propsWithDefaults, inputValue);

  const optionsPerSection = useOptionsPerSection(correctOptions);

  const actionRef: DSComboboxT.ActionRefVirtualized = {
    scrollTo: () => {},
  };

  const listRef = useRef<DSComboboxT.ActionRefVirtualized>(actionRef);

  // ===========================================================================
  // Scroll into view function
  // ===========================================================================
  const scrollOptionIntoView = useCallback(
    (dsId: string, opts: DSFastListT.ScrollToOptions = { align: 'center' } as DSFastListT.ScrollToOptions) => {
      listRef.current.scrollTo(
        correctOptions.findIndex((opt) => opt.dsId === dsId),
        opts,
      );
      requestAnimationFrame(() => {
        listRef.current.scrollTo(
          correctOptions.findIndex((opt) => opt.dsId === dsId),
          opts,
        );
      });
    },
    [correctOptions],
  );

  // ===========================================================================
  // Init focused option when opening the menu list
  // ===========================================================================

  const [focusOptionIdx, setFocusOptionIdx] = useState<string>('');

  useEffect(() => {
    if (inputValue && propsWithDefaults.forceFocusFirstOptionOnType) {
      const focusedValue = getFirstOption(correctOptions);
      setFocusOptionIdx(focusedValue);
      scrollOptionIntoView(focusedValue, { align: 'center' });
    }
  }, [correctOptions, inputValue, propsWithDefaults.forceFocusFirstOptionOnType, scrollOptionIntoView, selectedValues]);

  const setMenuState = useCallback(
    (
      newState: boolean,
      reason: string,
      e?: React.KeyboardEvent | React.MouseEvent | React.ChangeEvent<HTMLInputElement>,
    ) => {
      if (applyAriaDisabled) return;
      if (onMenuChange !== undefined) onMenuChange(newState, reason, e);
      setShowPopover(newState);
      if (!inputValue && newState && !focusOptionIdx && !isSelectedValueEmpty(selectedValues)) {
        const focusedValue = getFirstOption(correctOptions, selectedValues);
        setFocusOptionIdx(focusedValue);
        scrollOptionIntoView(focusedValue, { align: 'center' });
      } else {
        setFocusOptionIdx('');
      }
    },
    [applyAriaDisabled, onMenuChange, inputValue, focusOptionIdx, selectedValues, correctOptions, scrollOptionIntoView],
  );

  // We need to force the auto focus for the inline since we dont control the floating wrapper.
  useEffect(() => {
    if (propsWithDefaults.inline) {
      setMenuState(true, 'inline');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const shouldPreventContextMenuOpening = propsWithDefaults.readOnly && !propsWithDefaults.isMenuOpenableWithReadOnly;

  const ctx = useMemo(
    () => ({
      props: { ...propsWithDefaults },
      menuState,
      referenceElement,
      listRef,
      focusOptionIdx,
      selectedOptionsRef,
      controlsWrapperRef,
      inputValue,
      setInputValue,
      setMenuState,
      hasFocus,
      pillGroupRef,
      wrapperListRef,
      setHasFocus,
      setFocusOptionIdx,
      scrollOptionIntoView,
      setReferenceElement,
      setShowPopover,
      internalRef,
      correctOptions,
      instanceUid,
      optionsPerSection,
      shouldPreventContextMenuOpening,
    }),
    [
      scrollOptionIntoView,
      setMenuState,
      correctOptions,
      hasFocus,
      propsWithDefaults,
      inputValue,
      focusOptionIdx,
      wrapperListRef,
      menuState,
      pillGroupRef,
      referenceElement,
      selectedOptionsRef,
      controlsWrapperRef,
      listRef,
      internalRef,
      instanceUid,
      optionsPerSection,
      shouldPreventContextMenuOpening,
    ],
  );

  return ctx;
};
